// packages/api/src/hooks/useNotifications.ts
//
// Inbox de notificações do usuário autenticado (sininho / topbar).
// Contrato: docs/contracts/ESCR-public-quote-consultor-notify.md.
//
// Endpoints (JwtAuthGuard, self-escopados em currentUser.id):
//   GET   /notifications              → { count, unreadCount, rows }
//   GET   /notifications/unread-count → { count }
//   PATCH /notifications/:id/read     → AlamoNotification
//   PATCH /notifications/read-all     → { updated }

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AlamoNotification,
  ApiSuccess,
  NotificationsListFilters,
  NotificationsListResponse,
} from '@alamo/types';

import { apiClient } from '../client';
import { notificationsInboxKeys } from '../query-keys';

/**
 * Lista as notificações do usuário (recente primeiro). Faz polling leve a cada
 * 5min como fallback do Socket.IO (`notifications.created`, que invalida esta
 * query em tempo real quando disponível), mais refetch on-focus/reconnect.
 */
export function useNotifications(filters: NotificationsListFilters = {}) {
  const params: Record<string, unknown> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
    ...(filters.unreadOnly ? { unreadOnly: true } : {}),
  };

  return useQuery({
    queryKey: notificationsInboxKeys.list(params),
    queryFn: async (): Promise<NotificationsListResponse> => {
      const res = await apiClient.get<ApiSuccess<NotificationsListResponse>>('/notifications', {
        params,
      });
      return res.data.data;
    },
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Contagem de não lidas (badge do sininho). Endpoint barato — fallback do
 * Socket.IO quando o WS está indisponível.
 */
export function useUnreadNotificationsCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: notificationsInboxKeys.unreadCount(),
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<number> => {
      const res = await apiClient.get<ApiSuccess<{ count: number }>>('/notifications/unread-count');
      return res.data.data.count;
    },
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/** Marca UMA notificação do usuário como lida; invalida a inbox. */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AlamoNotification> => {
      const res = await apiClient.patch<ApiSuccess<AlamoNotification>>(`/notifications/${id}/read`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}

/** Marca TODAS as notificações do usuário como lidas; invalida a inbox. */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ updated: number }> => {
      const res = await apiClient.patch<ApiSuccess<{ updated: number }>>('/notifications/read-all');
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsInboxKeys.all });
    },
  });
}
